import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAddons } from "@/contexts/AddonsContext";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChartBar, Warning, ArrowRight, Briefcase, Rocket } from "@phosphor-icons/react";

import { devLog } from "@/lib/devLog";
/**
 * Compact plan/usage widget for the Owner / Admin dashboard.
 *
 * Renders progress bars for the four metered dimensions, a renewal
 * pill when the plan is within 14 days of expiry, an add-on badge
 * (active/inactive) for the Enterprise Workforce Suite, and — when the
 * workspace is on a trial or an expired plan — a subscribe CTA banner
 * that deep-links to the plan comparison page.
 */
export default function PlanUsageWidget() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const { hasEnterpriseWorkforce } = useAddons();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plan");
        setD(data);
      } catch (err) { devLog("plan fetch failed (probably not owner/admin)", err); }
    })();
  }, []);

  if (!d) return null;
  const { plan, usage, meta } = d;

  const metrics = [
    { label: "Students",  current: usage.students,   limit: plan.student_limit,   pct: meta.student_pct },
    { label: "Users",     current: usage.users,      limit: plan.user_limit,      pct: meta.user_pct },
    { label: "Branches",  current: usage.branches,   limit: plan.branch_limit,    pct: meta.branch_pct },
    { label: "Storage",   current: `${usage.storage_gb} GB`, limit: plan.storage_gb_limit === -1 ? -1 : `${plan.storage_gb_limit} GB`, pct: meta.storage_pct },
  ];

  const daysLeft = meta.days_remaining;
  const isTrial = plan.billing_cycle === "trial";
  const isExpired = typeof daysLeft === "number" && daysLeft < 0;
  const showRenewalBanner = typeof daysLeft === "number" && daysLeft <= 14 && !isTrial;

  return (
    <Card data-testid="plan-usage-widget">
      <CardContent className="p-5 space-y-4">
        {/* Header — plan name + billing cycle + price + View Plans link */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline mb-1 flex items-center gap-1.5">
              <ChartBar size={11} weight="duotone" /> Plan &amp; Usage
            </div>
            <div className="font-display text-xl font-bold tracking-tight flex items-center gap-2 flex-wrap">
              {plan.tier_name}
              <Badge className="bg-primary/10 text-primary border-primary/30 text-[10px]">{plan.billing_cycle}</Badge>
              {plan.monthly_price > 0 && (
                <span className="text-xs text-muted-foreground font-normal font-sans">
                  ₹{plan.monthly_price.toLocaleString()}/mo
                </span>
              )}
            </div>
            {plan.expires_on && !isTrial && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Renews {plan.expires_on}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/billing/subscription")}
            className="gap-1 text-xs"
            data-testid="plan-widget-manage"
          >
            View Plans <ArrowRight size={12} />
          </Button>
        </div>

        {/* Add-on badge — always visible so admins know the status at a glance */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            className={hasEnterpriseWorkforce
              ? "bg-emerald-100 text-emerald-800 border-emerald-200 gap-1"
              : "bg-muted text-muted-foreground border-border gap-1"}
            data-testid="widget-addon-badge"
          >
            <Briefcase size={11} weight="duotone" />
            Workforce Suite · {hasEnterpriseWorkforce ? "active" : "inactive"}
          </Badge>
        </div>

        {/* Trial / expired CTA banner */}
        {(isTrial || isExpired) && (
          <div
            className={`rounded-md border-2 p-3 flex items-start gap-2.5 ${
              isExpired ? "border-red-300 bg-red-50" : "border-primary/40 bg-primary/5"
            }`}
            data-testid="widget-subscribe-cta"
          >
            <Rocket
              size={18}
              weight="duotone"
              className={isExpired ? "text-red-600 mt-0.5" : "text-primary mt-0.5"}
            />
            <div className="flex-1 text-xs">
              <div className={`font-semibold ${isExpired ? "text-red-900" : "text-primary"}`}>
                {isExpired ? "Trial expired — subscribe now" : "You're on a free trial"}
              </div>
              <div className="text-muted-foreground mt-0.5">
                Subscribe to unlock the full workspace and lift usage limits.
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate("/billing/subscription")}
              data-testid="widget-subscribe-btn"
              className="flex-none"
            >
              Subscribe
            </Button>
          </div>
        )}

        {/* Renewal reminder (non-trial) */}
        {showRenewalBanner && (
          <div
            className={`rounded-md border p-2.5 text-xs flex items-center gap-2 ${daysLeft < 0 ? "border-red-200 bg-red-50 text-red-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
            data-testid="renewal-banner"
          >
            <Warning size={14} weight="duotone" />
            {daysLeft < 0
              ? <>Your plan expired <span className="font-semibold">{Math.abs(daysLeft)} days</span> ago.</>
              : <>Renewal due in <span className="font-semibold">{daysLeft} day{daysLeft !== 1 ? "s" : ""}</span> ({plan.expires_on}).</>
            }
          </div>
        )}

        {/* Usage bars */}
        <div className="grid grid-cols-2 gap-3">
          {metrics.map(m => {
            const unlimited = m.limit === -1;
            const overCap = !unlimited && typeof m.current === "number" && m.current >= m.limit;
            const near = m.pct >= 80 && m.pct < 100;
            const color = unlimited ? "bg-emerald-500" : overCap ? "bg-red-500" : near ? "bg-amber-500" : "bg-primary";
            return (
              <div key={m.label} className="space-y-1" data-testid={`widget-usage-${m.label.toLowerCase()}`}>
                <div className="flex items-baseline justify-between">
                  <div className="text-[11px] text-muted-foreground">{m.label}</div>
                  <div className="text-xs tabular-nums">
                    <span className={overCap ? "text-red-600 font-semibold" : "font-medium"}>{m.current}</span>
                    <span className="text-muted-foreground"> / {unlimited ? "∞" : m.limit}</span>
                  </div>
                </div>
                <Progress value={unlimited ? 100 : Math.min(m.pct, 100)} className="h-1.5" indicatorClassName={color} />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
